import * as React from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { supabase } from "@/integrations/supabase/client";
import { useServerFn } from "@tanstack/react-start";
import { motion } from "framer-motion";

import { provisionAdminAccount } from "@/lib/center.functions";
import { useAuth } from "@/lib/auth";
import { createPayment } from "@/lib/payos.functions";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { BrandLogo } from "@/components/brand-logo";
import { Loader2, ShieldCheck, Check, Loader, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { ThemeToggle } from "@/lib/theme";

type SignupSearch = { plan?: string; cycle?: "monthly" | "yearly" };

const PLAN_INFO: Record<string, { name: string; priceMonthly: string; priceYearly: string }> = {
  tutor: { name: "Gia sư", priceMonthly: "50.000đ/tháng", priceYearly: "500.000đ/năm" },
  center: { name: "Trung tâm", priceMonthly: "199.000đ/tháng", priceYearly: "2.000.000đ/năm" },
  school: { name: "Trường học", priceMonthly: "299.000đ/tháng", priceYearly: "3.000.000đ/năm" },
};

const SLUG_REGEX = /^[a-z][a-z0-9-]{2,28}[a-z0-9]$/;

function getSlugError(s: string): string {
  if (!s) return "";
  if (s.length < 4) return "Tên đăng nhập phải có ít nhất 4 ký tự";
  if (s.length > 30) return "Tên đăng nhập tối đa 30 ký tự";
  if (!/^[a-z]/.test(s)) return "Tên đăng nhập phải bắt đầu bằng chữ cái";
  if (!/^[a-z0-9-]+$/.test(s)) return "Chỉ dùng chữ thường, số và dấu gạch ngang";
  if (!/[a-z0-9]$/.test(s)) return "Không kết thúc bằng dấu gạch ngang";
  if (!SLUG_REGEX.test(s)) return "Tên đăng nhập không hợp lệ";
  return "";
}

export const Route = createFileRoute("/signup")({
  validateSearch: (s: Record<string, unknown>): SignupSearch => ({
    plan: typeof s.plan === "string" ? s.plan : undefined,
    cycle: s.cycle === "monthly" || s.cycle === "yearly" ? s.cycle : "yearly",
  }),
  head: () => ({
    meta: [
      { title: "Đăng ký tài khoản quản trị — Ezyclass" },
      { name: "description", content: "Tạo tài khoản quản trị Ezyclass để bắt đầu quản lý trung tâm, lớp học, học viên, học phí và lương giáo viên — cài đặt chỉ trong 2 phút." },
      { property: "og:title", content: "Đăng ký tài khoản quản trị — Ezyclass" },
      { property: "og:description", content: "Tạo tài khoản quản trị Ezyclass để bắt đầu quản lý trung tâm, lớp học và học viên." },
      { property: "og:url", content: "https://ezyclass.com/signup" },
    ],
    links: [{ rel: "canonical", href: "https://ezyclass.com/signup" }],
  }),

  component: SignupPage,
});

function SignupPage() {
  const { plan, cycle } = Route.useSearch();
  const navigate = useNavigate();
  const { refresh } = useAuth();

  const provisionAdminFn = useServerFn(provisionAdminAccount);
  const createPaymentFn = useServerFn(createPayment);
  const [name, setName] = React.useState("");
  const [slug, setSlug] = React.useState("");
  const [slugError, setSlugError] = React.useState("");
  const [checkingSlug, setCheckingSlug] = React.useState(false);
  const [password, setPassword] = React.useState("");
  const [showPassword, setShowPassword] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);

  const planInfo = plan ? PLAN_INFO[plan] : null;
  const planLabel = planInfo
    ? `${planInfo.name} — ${cycle === "yearly" ? planInfo.priceYearly : planInfo.priceMonthly}`
    : null;

  React.useEffect(() => {
    if (!SLUG_REGEX.test(slug)) return;
    setCheckingSlug(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await supabase
          .from("centers")
          .select("id")
          .eq("slug", slug)
          .maybeSingle();
        setSlugError(data ? "Tên này đã được sử dụng. Vui lòng chọn tên khác." : "");
      } finally {
        setCheckingSlug(false);
      }
    }, 400);
    return () => clearTimeout(t);
  }, [slug]);

  const slugValid = SLUG_REGEX.test(slug);
  const adminEmail = `${slug}@admin.ezyclass`;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!slugValid) { toast.error("Tên đăng nhập không hợp lệ"); return; }
    if (slugError) { toast.error(slugError); return; }
    if (password.length < 6) { toast.error("Mật khẩu phải ít nhất 6 ký tự"); return; }

    setSubmitting(true);
    try {
      const { data, error } = await supabase.auth.signUp({
        email: adminEmail,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/onboarding`,
          data: { display_name: name.trim() },
        },
      });
      if (error) throw error;
      if (!data.user) {
        toast.error("Không tạo được tài khoản. Vui lòng thử lại.");
        return;
      }

      if (data.session) {
        // CÓ session → provision ngay (atomic: role admin + center + link profile)
        try {
          await provisionAdminFn({ data: { name: name.trim(), slug } });
        } catch (provErr: unknown) {
          const msg = provErr instanceof Error ? provErr.message : String(provErr);
          console.error("provisionAdminAccount failed", provErr);
          toast.error(msg || "Không khởi tạo được tài khoản admin");
          setSubmitting(false);
          return;
        }

        await refresh();
        toast.success("Tạo tài khoản thành công!");

        if (planInfo && plan) {
          try {
            const result = await createPaymentFn({
              data: { plan: plan as "tutor" | "center" | "school", cycle: cycle || "yearly" },
            });
            if (result.checkoutUrl) {
              window.location.href = result.checkoutUrl;
              return;
            }
          } catch (checkoutErr: unknown) {
            const cmsg = checkoutErr instanceof Error ? checkoutErr.message : String(checkoutErr);
            console.error("PayOS checkout failed, continuing to onboarding", checkoutErr);
            toast.error(cmsg || "Không tạo được thanh toán. Bạn có thể nâng cấp sau trong mục Thanh toán.");
          }
        }

        navigate({ to: "/onboarding" });
      } else {
        // KHÔNG có session (email confirmation bật) → lưu pending để provision sau khi login.
        if (typeof window !== "undefined") {
          try {
            window.localStorage.setItem(
              "ezyclass_pending_center",
              JSON.stringify({ name: name.trim(), slug }),
            );
          } catch { /* ignore quota errors */ }
        }
        toast.success("Đã gửi email xác nhận! Kiểm tra hộp thư và bấm link để kích hoạt.", { duration: 8000 });
        navigate({ to: "/login" });
      }

    } catch (err: any) {
      const msg = err?.message ?? "";
      if (msg.toLowerCase().includes("already registered") || msg.toLowerCase().includes("already exists") || msg.toLowerCase().includes("user already")) {
        toast.error("Tên đăng nhập này đã tồn tại. Hãy chọn tên khác.");
      } else {
        toast.error(msg || "Không tạo được tài khoản");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-[100dvh] grid lg:grid-cols-2 bg-white dark:bg-slate-900 relative">
      <div className="absolute top-4 right-4 z-50"><ThemeToggle /></div>
      {/* Brand panel */}
      <div className="hidden lg:flex flex-col justify-between bg-slate-950 p-12 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full bg-blue-600/10 blur-[120px]" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full bg-indigo-600/10 blur-[100px]" />

        <Link to="/" className="relative z-10 flex items-center gap-2.5 w-fit">
          <BrandLogo onDark size="size-9" />
          <span className="font-display font-semibold text-lg text-white tracking-tight">Ezyclass</span>
        </Link>

        <div className="relative z-10 max-w-md">
          <div className="text-xs uppercase tracking-wider text-white/40 font-medium mb-4">
            Cho trung tâm Việt Nam
          </div>
          <h2 className="font-display text-3xl md:text-4xl text-white font-bold tracking-tight leading-tight">
            Bắt đầu quản lý<br />trung tâm của bạn<br />
            <span className="text-white/60 font-normal">trong vài phút.</span>
          </h2>
          <p className="mt-5 text-sm text-white/50 leading-relaxed">
            Tạo tài khoản admin, mời giáo viên và học sinh — Ezyclass lo phần còn lại.
          </p>
        </div>

        <div className="relative z-10 text-xs text-white/30">© 2026 Ezyclass</div>
      </div>

      {/* Form */}
      <div className="flex items-center justify-center p-6 md:p-12">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.32, 0.72, 0, 1] }}
          className="w-full max-w-[420px]"
        >
          <div className="lg:hidden flex items-center gap-2.5 mb-10">
            <Link to="/" className="flex items-center gap-2.5">
              <BrandLogo size="size-9" />
              <span className="font-display font-semibold text-lg tracking-tight">Ezyclass</span>
            </Link>
          </div>

          <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900 dark:text-white">Tạo tài khoản admin</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-2">Quản lý trung tâm của bạn với Ezyclass.</p>

          {planLabel && (
            <div className="flex items-center gap-2 mt-4 px-3.5 py-2.5 rounded-xl bg-emerald-50 dark:bg-emerald-500/10 ring-1 ring-emerald-100 text-sm text-emerald-700 dark:text-emerald-300">
              <Check className="size-4 shrink-0" strokeWidth={1.5} />
              <span>Gói <strong>{planLabel}</strong> · Thanh toán sau khi tạo</span>
            </div>
          )}

          <div className="flex items-center gap-2 mt-3 px-3.5 py-2.5 rounded-xl bg-blue-50 dark:bg-blue-500/10 ring-1 ring-blue-100 text-xs text-blue-700 dark:text-blue-300">
            <ShieldCheck className="size-4 shrink-0" strokeWidth={1.5} />
            <span>Giáo viên & học viên sẽ được bạn tạo tài khoản sau khi đăng nhập.</span>
          </div>

          <form onSubmit={handleSubmit} className="mt-6 space-y-4">
            <div>
              <label htmlFor="name" className="text-sm font-medium text-slate-700 dark:text-slate-200">Tên trung tâm / tên của bạn</label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                minLength={2}
                maxLength={120}
                placeholder="VD: Trung tâm Anh ngữ Sunrise"
                className="mt-1.5 h-11 rounded-xl bg-slate-50 dark:bg-slate-950 border-slate-200 dark:border-slate-800 focus-visible:bg-white dark:bg-slate-900 focus-visible:ring-2 focus-visible:ring-blue-500/20 focus-visible:border-blue-500 transition-all"
              />
            </div>

            <div>
              <label htmlFor="slug" className="text-sm font-medium text-slate-700 dark:text-slate-200">Email đăng nhập admin *</label>
              <div className="flex mt-1.5">
                <Input
                  id="slug"
                  value={slug}
                  onChange={(e) => {
                    const v = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "");
                    setSlug(v);
                    setSlugError("");
                  }}
                  placeholder="tentrungtam"
                  className="h-11 rounded-l-xl rounded-r-none border-r-0 bg-slate-50 dark:bg-slate-950 border-slate-200 dark:border-slate-800 flex-1 focus-visible:bg-white dark:bg-slate-900 focus-visible:ring-2 focus-visible:ring-blue-500/20 focus-visible:border-blue-500 transition-all"
                  maxLength={30}
                  required
                />
                <div className="flex items-center px-3.5 h-11 bg-slate-100 dark:bg-slate-800 border border-l-0 border-slate-200 dark:border-slate-800 rounded-r-xl text-sm text-slate-500 dark:text-slate-400 select-none font-mono">
                  @admin.ezyclass
                </div>
              </div>
              {slug && !slugValid && (
                <p className="text-xs text-red-600 mt-1.5">{getSlugError(slug)}</p>
              )}
              {slugError && <p className="text-xs text-red-600 mt-1.5">{slugError}</p>}
              {checkingSlug && slugValid && (
                <p className="text-xs text-slate-500 dark:text-slate-400 flex items-center gap-1 mt-1.5">
                  <Loader className="size-3 animate-spin" strokeWidth={1.5} /> Đang kiểm tra...
                </p>
              )}
              {slug && slugValid && !slugError && !checkingSlug && (
                <p className="text-xs text-emerald-600 mt-1.5">✓ Email đăng nhập: <strong>{adminEmail}</strong></p>
              )}
            </div>

            <div>
              <label htmlFor="password" className="text-sm font-medium text-slate-700 dark:text-slate-200">Mật khẩu (tối thiểu 6 ký tự)</label>
              <div className="relative mt-1.5">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                  autoComplete="new-password"
                  placeholder="••••••••"
                  className="h-11 rounded-xl bg-slate-50 dark:bg-slate-950 border-slate-200 dark:border-slate-800 pr-10 focus-visible:bg-white dark:bg-slate-900 focus-visible:ring-2 focus-visible:ring-blue-500/20 focus-visible:border-blue-500 transition-all"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  tabIndex={-1}
                  aria-label={showPassword ? "Ẩn mật khẩu" : "Hiện mật khẩu"}
                  aria-pressed={showPassword}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:text-slate-400 transition"
                >
                  {showPassword ? <EyeOff className="size-4" strokeWidth={1.5} /> : <Eye className="size-4" strokeWidth={1.5} />}
                </button>

              </div>
            </div>

            <Button
              type="submit"
              disabled={submitting || !slugValid || !!slugError || checkingSlug}
              className="w-full h-11 rounded-xl bg-slate-900 hover:bg-slate-800 font-medium active:scale-[0.99] transition-all duration-500 ease-[cubic-bezier(0.32,0.72,0,1)]"
            >
              {submitting ? <Loader2 className="size-4 animate-spin" /> : (planInfo ? "Tạo tài khoản & Thanh toán" : "Tạo tài khoản")}
            </Button>
          </form>

          <p className="text-center text-sm text-slate-500 dark:text-slate-400 mt-6">
            Đã có tài khoản?{" "}
            <Link to="/login" className="text-blue-600 hover:text-blue-700 dark:text-blue-300 font-medium">
              Đăng nhập
            </Link>
          </p>

          <div className="mt-8 pt-6 border-t border-slate-100 dark:border-slate-800 text-xs text-slate-400 dark:text-slate-500">
            <Link to="/" className="hover:text-slate-600 dark:text-slate-400 transition">← Về trang chủ</Link>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
